/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import intlTelInput from 'intl-tel-input';
import { catchError, EMPTY, finalize, of, tap } from 'rxjs';
import { AUTH_STATE_TOKEN } from '~console/app/~shared/constants/global.constants';
import { ToastService } from '~console/app/~shared/services/toast.service';
import { validatePhoneNumber } from '~console/app/~shared/utils/validators';
import { attachDialCodePrefill } from '~console/app/~shared/utils/phone.utils';
import { AccountService } from '~console/app/~shared/services/account.service';
import { BaseAuthComponent } from '../../abstract-components/base-auth.component';

@Component({
  selector: 'personal-infos-form',
  standalone: false,
  templateUrl: './personal-infos-form.component.html',
  styleUrl: './personal-infos-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonalInfosFormComponent
  extends BaseAuthComponent
  implements AfterViewInit, OnDestroy
{
  private _accountService = inject(AccountService);
  private _toastService = inject(ToastService);

  intlInputPhone = viewChild<ElementRef>('phoneNumberInput');
  phoneNumberIntlPluginRef?: intlTelInput.Plugin;

  identifiedCompany$ = this._authFacade.identifiedCompany$;
  statePropertyPath = `${AUTH_STATE_TOKEN.getName()}.registration.registrationFormValues`;

  isSubmitting: boolean = false;
  validatePhoneNumberFn = validatePhoneNumber;

  signupForm = new FormGroup({
    /*  fullName: new FormControl('', {
      validators: [Validators.required, Validators.minLength(3)],
      nonNullable: true
    }), */
    email: new FormControl('', {
      validators: [Validators.required, Validators.email],
      nonNullable: true
    }) /* ,
    phoneNumber: new FormControl('', {
      validators: [
        Validators.required,
        phoneNumberValidator({
          country: this._constantsService.defaultCountry
        })
      ],
      nonNullable: true
    }) */
  });

  ngAfterViewInit(): void {
    if (!this._isPlatformBrowser) return;
    this.initPhoneNumberInputs();
  }

  ngOnDestroy(): void {
    if (this.phoneNumberIntlPluginRef) {
      this.phoneNumberIntlPluginRef.destroy();
    }
  }

  initPhoneNumberInputs(): void {
    if (!this.intlInputPhone()?.nativeElement) {
      return;
    }

    this.phoneNumberIntlPluginRef = intlTelInput(
      this.intlInputPhone()?.nativeElement,
      {
        allowDropdown: true,
        formatOnDisplay: true,
        nationalMode: false,
        autoInsertDialCode: true,
        separateDialCode: true,
        initialCountry: this._constantsService.defaultCountry.toLowerCase(),
        customContainer: 'country',
        dropdownContainer: document.body
      }
    );
    attachDialCodePrefill(
      this.intlInputPhone()?.nativeElement,
      this.phoneNumberIntlPluginRef
    );
  }

  proceedPersonalInfos(): void {
    /*  const nameParts =
      this.fullName?.value?.trim().split(' ').filter(Boolean) || [];

    if (nameParts.length < 2) {
      this._markControlError('fullName', 'Veuillez entrer vos prénoms!');
      return;
    } */

    this.isSubmitting = true;

    this._accountService
      .checkUserPropertyValidity({
        // phoneNumber: this.phoneNumber?.value,
        email: this.email?.value
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._authFacade.patchUserExistenceStatus(false);
            this._authFacade.getRegistrationOneTimePassword();
            this._eventService.emit({ type: 'GO_TO_NEXT_STEP' });
          }
        }),
        catchError(err => {
          if (err?.status !== 400) return of(false);

          /*  const phoneViolation = this._getViolationForProperty(
            err,
            'phoneNumber'
          );
          if (phoneViolation) {
            this._markControlError(
              'phoneNumber',
              phoneViolation.message || 'Numéro de téléphone invalide.'
            );
            return EMPTY;
          } */

          const emailViolation = this._getViolationForProperty(err, 'email');
          if (emailViolation) {
            this._authFacade.patchUserExistenceStatus(true);
            this._toastService.open({
              content: `Cette adresse email est déjà associée à un compte existant.
              Veuillez vous connecter avec votre mot de passe.`,
              type: 'warning',
              config: {
                duration: 7000
              }
            });
            this._eventService.emit({ type: 'GO_TO_NEXT_STEP' });
            return EMPTY;
          }

          return of(false);
        }),
        finalize(() => {
          this.isSubmitting = false;
          this._cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  goBack(): void {
    this._eventService.emit({ type: 'GO_TO_PREVIOUS_STEP' });
  }

  private _markControlError(controlName: string, errorMessage: string): void {
    this.isSubmitting = false;
    const control = this.signupForm.get(controlName);
    if (control) {
      control.markAsTouched();
      control.setErrors({ customError: errorMessage });
      setTimeout(() => this._cdRef.detectChanges(), 250);
    }
  }

  private _getViolationForProperty(
    error: any,
    property: string
  ): { propertyPath?: string; message?: string } | null {
    const violations = error?.error?.violations;
    const violation = violations?.find((v: any) =>
      v?.propertyPath?.includes(property)
    );

    if (!violation) return null;

    return {
      propertyPath: violation.propertyPath,
      message: violation.title || error?.error?.detail
    };
  }

  get fullName(): AbstractControl | null {
    return this.signupForm.get('fullName');
  }

  get email(): AbstractControl | null {
    return this.signupForm.get('email');
  }

  get phoneNumber(): AbstractControl | null {
    return this.signupForm.get('phoneNumber');
  }
}
